import {
  ControlDeviceTableBody,
  ControlDeviceTablePlaceholder,
  DSTAdjustmentModal,
  DeviceTableBody,
  DeviceTableHeader,
  DeviceTablePlaceholder,
  DeviceTableTitle,
  DeviceWithInstalledPower,
  LineChart,
  ProductTable,
  ProductionOverview,
  SelectedCell,
  SpotPriceResult,
  dispatchNativeInputEvent,
  getCellWidthSum,
} from '@e2m-customer-portal/domains';
import { ForecastSlice, useLocale } from '@e2m-customer-portal/hooks';
import { InputFieldType, convertMeasure, getInputFieldTypes } from '@e2m-customer-portal/shared';
import { DowntimeRevokeModal, ErrorComponent } from '@e2m-customer-portal/ui';
import clsx from 'clsx';
import { Interval, addMinutes, endOfDay, isBefore, isPast, isToday, isWeekend } from 'date-fns';
import {
  EditableMarket,
  ForecastProperty,
  ForecastTarget,
  MarketLocation,
  ProductService,
  ProductServiceType,
  Resolution,
  SpotPriceForecast,
} from 'generated/graphql';
import React, { Fragment, ReactElement, Suspense, useCallback, useEffect, useMemo, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';

export interface SelectHandle {
  active: boolean;
  cellWidth: number;
  cellHeight: number;
  column: number;
  row: number;
  targetColumn: number;
  targetRow: number;
  selectionType?: 'fill' | 'select' | 'copy';
}

interface DeviceTableProps {
  deviceIndex: number;
  marketLocation: MarketLocation;
  device: DeviceWithInstalledPower;
  interval: Interval;
  deadlineDate?: Date;
  slices: readonly ForecastSlice[];
  controlEnergySlices: readonly ForecastSlice[];
  daysInForecast: Date[];
  spotPriceForecast?: SpotPriceForecast;
  showSpotPrices?: boolean;
  spotPrice: (sliceDate: Date) => SpotPriceResult | undefined;
  readOnly?: boolean;
  resetTrigger: boolean;
  isBeingEdited: boolean;
  editedSlices: Set<string>;
  editedDevices: string[];
  overviewMap: Map<string, (HTMLInputElement | null)[][]>;
  dstSliceValues?: Map<string, Array<Array<{ slice: ForecastSlice; value: string }>>>;
  setEditedSlices: React.Dispatch<React.SetStateAction<Set<string>>>;
  setErroneousSlices: React.Dispatch<React.SetStateAction<Set<string>>>;
  setOverviewMap: React.Dispatch<React.SetStateAction<Map<string, Array<Array<HTMLInputElement | null>>>>>;
  setDSTSliceValues?: React.Dispatch<
    React.SetStateAction<Map<string, Array<Array<{ slice: ForecastSlice; value: string }>>>>
  >;
  saveChanges: () => void;
  productTable: ProductTable;
  deviceMap?: (HTMLInputElement | null)[][];
  onClick?: () => void;
  currentForecastProperty?: ForecastProperty;
  sortedProductServices?: ProductService[];
}

export function DeviceTable({
  deviceIndex,
  marketLocation,
  device,
  interval,
  deadlineDate,
  slices,
  controlEnergySlices,
  daysInForecast,
  spotPriceForecast,
  showSpotPrices,
  spotPrice,
  readOnly,
  resetTrigger,
  isBeingEdited,
  editedSlices,
  editedDevices,
  overviewMap,
  dstSliceValues,
  setEditedSlices,
  setErroneousSlices,
  setOverviewMap,
  setDSTSliceValues,
  saveChanges,
  productTable,
  currentForecastProperty,
  sortedProductServices,
}: Readonly<DeviceTableProps>): ReactElement<DeviceTableProps> {
  const { code: locale } = useLocale();
  const [t] = useTranslation();
  const [isExpanded, setIsExpanded] = useState(deviceIndex === 0);
  const [isHighlighted, setIsHighlighted] = useState(false);
  const [revokeDowntimeInterval, setRevokeDowntimeInterval] = useState<Interval | null>(null);
  const [selectHandle, setSelectHandle] = useState<SelectHandle>({
    active: false,
    cellWidth: 0,
    cellHeight: 0,
    column: 0,
    row: 0,
    targetColumn: 0,
    targetRow: 0,
  });
  const [selectedCells, setSelectedCells] = useState<SelectedCell[]>([]);

  const isFifteenMinutesResolution = currentForecastProperty?.resolution === Resolution.FIFTEEN_MINUTES;

  const showIntraday = sortedProductServices?.some(
    (service) =>
      service.type === ProductServiceType.INTRADAY_MARKETING ||
      service.type === ProductServiceType.INTRADAY_OPTIMIZATION,
  );

  const deviceMap = overviewMap.get(device.id);
  if (!deviceMap) throw Error(`Data Error: Table overview for device with id "${device.id}" not found`);

  // Derived visual representation of the select handle
  const visualHandle = useMemo(() => {
    if (!selectHandle.cellHeight && !selectHandle.cellWidth) return { left: 0, top: 0, width: 0, height: 0 };

    const column = Math.min(selectHandle.targetColumn, selectHandle.column);
    const row = Math.min(selectHandle.targetRow, selectHandle.row);

    const boundingRect = deviceMap[row][column]?.getBoundingClientRect();

    const selectedRows = Math.abs(selectHandle.row - selectHandle.targetRow) + 1;

    return {
      left: boundingRect ? boundingRect.left + window.scrollX - 2 : 0,
      top: boundingRect ? boundingRect.top + window.scrollY - 1 : 0,
      width: getCellWidthSum(row, selectHandle.column, selectHandle.targetColumn, selectHandle.cellWidth, deviceMap),
      height: selectedRows * selectHandle.cellHeight + 1,
    };
  }, [deviceMap, selectHandle]);

  const collectSelectedCells = useCallback(() => {
    setSelectHandle((selectHandle) => {
      const collectedCells = [];

      for (
        let row = Math.min(selectHandle.row, selectHandle.targetRow);
        row <= Math.max(selectHandle.row, selectHandle.targetRow);
        row++
      ) {
        for (
          let column = Math.min(selectHandle.column, selectHandle.targetColumn);
          column <= Math.max(selectHandle.column, selectHandle.targetColumn);
          column++
        ) {
          // Copy value 0 for DST button and downtimes
          if (!deviceMap[row][column]) {
            collectedCells.push({ row, column, value: '0' });
          } else {
            collectedCells.push({ row, column, value: deviceMap[row][column]?.value });
          }
        }
      }

      setSelectedCells(collectedCells);

      return { ...selectHandle, active: false };
    });
  }, [deviceMap]);

  const commitFillHandleChanges = useCallback(() => {
    setSelectHandle((selectHandle) => {
      const value = deviceMap[selectHandle.row][selectHandle.column]?.value;

      for (
        let row = Math.min(selectHandle.row, selectHandle.targetRow);
        row <= Math.max(selectHandle.row, selectHandle.targetRow);
        row++
      ) {
        for (
          let column = Math.min(selectHandle.column, selectHandle.targetColumn);
          column <= Math.max(selectHandle.column, selectHandle.targetColumn);
          column++
        ) {
          const input = deviceMap[row][column];
          // Skip source cell, summertime DST button, downtimes and read-only cells
          if ((row === selectHandle.row && column === selectHandle.column) || !input || input.readOnly) continue;

          dispatchNativeInputEvent(input, value);
        }
      }

      return { ...selectHandle, active: false, targetRow: selectHandle.row, targetColumn: selectHandle.column };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceMap, selectHandle.column, selectHandle.row, selectHandle.targetColumn, selectHandle.targetRow]);

  const scrollIfNeeded = (event: MouseEvent) => {
    if (window.innerHeight - event.y < 150) scrollBy({ behavior: 'smooth', top: 4 * selectHandle.cellHeight });
    else if (event.y < 350) scrollBy({ behavior: 'smooth', top: -4 * selectHandle.cellHeight });
  };

  useEffect(() => {
    if (selectHandle.active) {
      window.addEventListener(
        'mouseup',
        selectHandle.selectionType === 'fill' ? commitFillHandleChanges : collectSelectedCells,
      );
      window.addEventListener('mouseover', scrollIfNeeded);
    } else {
      window.removeEventListener('mouseup', commitFillHandleChanges);
      window.removeEventListener('mouseup', collectSelectedCells);
      window.removeEventListener('mouseover', scrollIfNeeded);
    }

    return () => {
      window.removeEventListener('mouseup', commitFillHandleChanges);
      window.removeEventListener('mouseup', collectSelectedCells);
      window.removeEventListener('mouseover', scrollIfNeeded);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectHandle.active]);

  const handleClick = () => {
    if (editedDevices.includes(device.id)) {
      setIsHighlighted(true);
      setTimeout(() => setIsHighlighted(false), 1500);
      deviceMap[Math.floor(deviceMap.length / 2)][0]?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    } else {
      setIsExpanded(!isExpanded);
    }
  };

  const handleSelectAll = useCallback(() => {
    const lastColumn = deviceMap[0].length - 1;
    const lastRow = deviceMap.length - 1;
    const allCells = new Array<SelectedCell>();

    for (let row = 0; row <= lastRow; row++) {
      for (let column = 0; column <= lastColumn; column++) {
        allCells.push({ row: row, column: column, value: deviceMap[row][column]?.value ?? '0' });
      }
    }
    setSelectedCells(allCells);

    const lastCellIndex = deviceMap[deviceMap.length - 1].length - 1;
    const leftMostCell = deviceMap.find((row) => row[0])?.at(0);
    const topMostCell = deviceMap[0].find((column) => column);
    const rightMostCell = deviceMap.findLast((row) => row[lastCellIndex])?.at(lastCellIndex);
    const bottomMostCell = deviceMap[deviceMap.length - 1].findLast((column) => column);

    // There are no cells in the first/last day or at the first/last hour of any day in the interval that aren’t a downtime :(
    if (!leftMostCell || !topMostCell || !rightMostCell || !bottomMostCell) return;

    const boundingRect = leftMostCell.getBoundingClientRect();
    if (!boundingRect) return;

    setSelectHandle({
      active: false,
      cellWidth: boundingRect.width,
      cellHeight: boundingRect.height + 1,
      column: 0,
      row: 0,
      targetColumn: lastColumn,
      targetRow: lastRow,
      selectionType: 'select',
    });
  }, [deviceMap]);

  const pasteCopiedCells = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>, startRow: number, startColumn: number) => {
      const savedValues = JSON.parse(window.localStorage.getItem('COPIED_PRODUCTION') ?? '[]') as Array<SelectedCell>;
      if (!savedValues.length) return;

      const indexOfLastRow = savedValues[savedValues.length - 1].row - savedValues[0].row;
      const indexOfLastColumn = savedValues[savedValues.length - 1].column - savedValues[0].column;
      let cellCounter = 0;
      for (let row = 0; row <= indexOfLastRow; row++) {
        for (let column = 0; column <= indexOfLastColumn; column++) {
          // Skip summertime DST button and downtimes
          const cell = deviceMap[row + startRow]?.at(column + startColumn);
          if (!cell || cell.readOnly || cell.disabled) {
            cellCounter++;
            continue;
          }

          dispatchNativeInputEvent(deviceMap[row + startRow][column + startColumn], savedValues[cellCounter].value);
          cellCounter++;
        }
      }

      event.preventDefault();
    },
    [deviceMap],
  );

  const selectColumn = useCallback(
    (index: number) => (event: React.MouseEvent) => {
      event.stopPropagation();
      setSelectedCells([]);

      let focusedRow = null;
      let skippedRows = 0;
      for (const row of deviceMap) {
        if (row[index] && !row[index]?.readOnly && !row[index]?.disabled) {
          row[index]?.focus();
          focusedRow = row;
          break;
        }
        skippedRows += 1;
      }
      if (!focusedRow) return;

      const boundingRect = focusedRow[index]?.getBoundingClientRect();
      if (!boundingRect) return;

      setSelectHandle({
        active: true,
        cellWidth: boundingRect.width,
        cellHeight: boundingRect.height + 1,
        column: index,
        row: skippedRows,
        targetColumn: index,
        targetRow: deviceMap.length - 1,
        selectionType: 'select',
      });
      event.preventDefault();
    },
    [deviceMap],
  );

  const expandColumnSelection = (index: number) => (event: React.MouseEvent) => {
    // Do nothing if the mouse is just moving casually
    if (!event.buttons) return;

    setSelectHandle((selectHandle) => ({
      ...selectHandle,
      targetColumn: index,
    }));
  };

  const [dstAdjustmentModalIsOpen, setDSTAdjustmentModalIsOpen] = useState(false);
  const toggleDSTModal = () => setDSTAdjustmentModalIsOpen((isOpen) => !isOpen);
  const intervalHasMultipleTimezones = new Set(slices.map((s) => s.date.getTimezoneOffset())).size > 1;
  const dstSlices = slices.filter(
    (slice, index) =>
      intervalHasMultipleTimezones &&
      (slice.date.getDate() === slices[index + 1]?.date.getDate() ||
        slice.date.getDate() === slices[index - 1]?.date.getDate()),
  );

  useEffect(() => {
    if (!setDSTSliceValues) return;

    setDSTSliceValues((dstSliceValues) => {
      const sliceValues = new Map(dstSliceValues);
      if (!dstSlices.length) {
        sliceValues.set(device.id, []);
        return sliceValues;
      }

      const values = new Array<Array<{ slice: ForecastSlice; value: string }>>();
      for (let index = 0; index < dstSlices.length / 2; index++) {
        values.push(new Array<{ slice: ForecastSlice; value: string }>());
      }

      for (let index = 0; index < dstSlices.length; index++) {
        const sliceValue = dstSlices[index].fields.find((value) => value.type === InputFieldType.PRODUCTION)?.value;
        values[Math.floor(index / 2)].push({
          slice: dstSlices[index],
          value:
            sliceValue !== undefined && sliceValue?.value !== null
              ? String(convertMeasure(sliceValue, 'kW').value)
              : '',
        });
      }
      return sliceValues.set(device.id, values);
    });
  }, [interval, resetTrigger]);

  return (
    <div className="first-of-type:mt-0 mt-8 bg-white">
      <DowntimeRevokeModal
        device={device}
        currentForecastProperty={currentForecastProperty}
        revokeDowntimeInterval={revokeDowntimeInterval}
        setRevokeDowntimeInterval={setRevokeDowntimeInterval}
      />
      {!!dstSlices.length && (
        <Suspense fallback={<></>}>
          <DSTAdjustmentModal
            open={dstAdjustmentModalIsOpen}
            toggleModal={toggleDSTModal}
            marketLocation={marketLocation}
            device={device}
            interval={interval}
            deadlineDate={deadlineDate}
            slices={dstSlices}
            dstSliceValues={dstSliceValues}
            setDSTSliceValues={setDSTSliceValues}
            showSpotPrices={showSpotPrices}
            spotPrice={spotPrice}
            latestHistoricalSpotPrice={spotPriceForecast?.latestHistoricalSpotPrice as Date | null | undefined}
            readOnly={
              (marketLocation.marketingEnd != null && !isBefore(dstSlices[0].date, marketLocation.marketingEnd)) ||
              readOnly
            }
            resetTrigger={resetTrigger}
            editedSlices={editedSlices}
            setEditedSlices={setEditedSlices}
            setRevokeDowntimeInterval={setRevokeDowntimeInterval}
            currentForecastProperty={currentForecastProperty}
          />
        </Suspense>
      )}
      <DeviceTableTitle
        device={device}
        marketLocation={marketLocation}
        interval={interval}
        slices={slices}
        disableAssistant={
          isBeingEdited || readOnly || (marketLocation.marketingEnd != null && isPast(marketLocation.marketingEnd))
        }
        currentForecastProperty={currentForecastProperty}
      />
      <ErrorBoundary fallback={<ErrorComponent />}>
        <div className="h-60 flex-col content-end py-4">
          <LineChart
            device={device}
            slices={slices}
            controlEnergySlices={controlEnergySlices}
            interval={interval}
            marketLocationName={marketLocation.name}
            spotPriceForecast={spotPriceForecast}
            dayAheadDeadlineDate={deadlineDate}
            isControlEnergyForecast={productTable !== 'production'}
            currentForecastProperty={currentForecastProperty}
            showIntraday={showIntraday}
          />
        </div>
      </ErrorBoundary>
      <DeviceTableHeader
        marketLocation={marketLocation}
        device={device}
        interval={interval}
        slices={slices}
        deviceMap={overviewMap.get(device.id)}
        open={isExpanded}
        disabled={editedDevices.includes(device.id)}
        onClick={handleClick}
        currentForecastProperty={currentForecastProperty}
        isFifteenMinutesResolution={isFifteenMinutesResolution}
      />
      <table className="border-spacing-0 bg-white [container:table_/_size]" data-testid={device.name}>
        <colgroup>
          <col span={2} />
          {daysInForecast.map((day) => (
            <col
              key={`colgroup-${device.id}-${day.getDate()}`}
              style={{ width: `${100 / daysInForecast.length}cqw` }}
            />
          ))}
          <col className="min-w-8" />
        </colgroup>
        <thead data-testid="device-table-head" className="relative">
          {(productTable === 'production' || isExpanded) && (
            <tr>
              <th
                className="min-w-40 box-border border-0 border-y border-solid border-divider px-6 py-2 text-start text-sm font-medium leading-none"
                colSpan={2}
              >
                {t('MARKET_LOCATION_FORECAST_TABLE_DATE')}
              </th>
              {daysInForecast.map((date, index) => {
                const isAfterDayAheadDeadline = deadlineDate && isBefore(endOfDay(date), deadlineDate);
                const isAfterIntradayDeadline = isBefore(
                  date,
                  addMinutes(
                    new Date(),
                    device.deadlines.find((deadline) => deadline.type === InputFieldType.PRODUCTION)?.deadline
                      ?.minutesBeforeIntradayDelivery ?? 0,
                  ),
                );
                const isIntradayEditable =
                  currentForecastProperty?.target === ForecastTarget.VPP &&
                  currentForecastProperty?.editableMarkets.includes(EditableMarket.INTRADAY);
                const isAfterDeadline =
                  (isIntradayEditable && isAfterIntradayDeadline) || (!isIntradayEditable && isAfterDayAheadDeadline);
                return (
                  <Fragment key={`date-${device.id}-${date.toISOString()}`}>
                    <th
                      className={clsx(
                        'border border-r-0 border-solid border-divider p-0 text-sm font-normal leading-4',
                        {
                          'bg-blue-grey-200/5': isWeekend(date),
                          'text-black/60': isAfterDeadline,
                          'cursor-cell': !isAfterDeadline && !readOnly,
                        },
                      )}
                      onMouseDown={selectColumn(index)}
                      onMouseMove={expandColumnSelection(index)}
                    >
                      <div className={clsx('w-full h-full py-2', { 'bg-green-50/40': isToday(date) })}>
                        <span
                          className={
                            isToday(date) ? 'px-2 my-auto leading-none rounded-full bg-green-200 text-white' : undefined
                          }
                        >
                          {date
                            .toLocaleDateString(locale, {
                              weekday: 'short',
                              day: '2-digit',
                              month: '2-digit',
                            })
                            .replace(/(?<=[A-Za-z])\./, '')}
                        </span>
                      </div>
                    </th>
                    {index === daysInForecast.length - 1 && (
                      <th
                        className={clsx('border-0 border-y border-solid border-divider', {
                          'bg-blue-grey-200/5': isWeekend(date),
                          'text-black/60': isAfterDeadline,
                        })}
                      />
                    )}
                  </Fragment>
                );
              })}
            </tr>
          )}
          {productTable === 'production' && (
            <ProductionOverview
              deviceId={device.id}
              overviewMap={overviewMap}
              slices={slices}
              editedSlices={editedSlices}
              days={daysInForecast}
              isFifteenMinutesResolution={isFifteenMinutesResolution}
              shouldUseLiveStats={isExpanded && isBeingEdited}
            />
          )}
        </thead>
        {isExpanded && (
          <>
            {productTable === 'production' ? (
              <Suspense
                fallback={
                  <DeviceTablePlaceholder
                    daysInForecast={daysInForecast}
                    isFifteenMinutesResolution={isFifteenMinutesResolution}
                  />
                }
              >
                <DeviceTableBody
                  marketLocation={marketLocation}
                  device={device}
                  interval={interval}
                  deadlineDate={deadlineDate}
                  slices={slices}
                  dstSliceValues={dstSliceValues}
                  daysInForecast={daysInForecast}
                  spotPriceForecast={spotPriceForecast}
                  showSpotPrices={showSpotPrices}
                  spotPrice={spotPrice}
                  readOnly={readOnly}
                  resetTrigger={resetTrigger}
                  editedSlices={editedSlices}
                  overviewMap={overviewMap}
                  isHighlighted={isHighlighted}
                  selectHandle={selectHandle}
                  selectedCells={selectedCells}
                  setRevokeDowntimeInterval={setRevokeDowntimeInterval}
                  setEditedSlices={setEditedSlices}
                  setErroneousSlices={setErroneousSlices}
                  setIsHighlighted={setIsHighlighted}
                  setOverviewMap={setOverviewMap}
                  setSelectHandle={setSelectHandle}
                  setSelectedCells={setSelectedCells}
                  handleSelectAll={handleSelectAll}
                  pasteCopiedCells={pasteCopiedCells}
                  saveChanges={saveChanges}
                  currentForecastProperty={currentForecastProperty}
                  toggleDSTModal={toggleDSTModal}
                />
              </Suspense>
            ) : (
              <Suspense
                fallback={
                  <ControlDeviceTablePlaceholder
                    daysInForecast={daysInForecast}
                    products={getInputFieldTypes(device.products)}
                  />
                }
              >
                <ControlDeviceTableBody
                  marketLocation={marketLocation}
                  device={device}
                  selectedProducts={new Set(getInputFieldTypes(device.products, 'bands'))}
                  slices={controlEnergySlices}
                  interval={interval}
                  daysInForecast={daysInForecast}
                  readOnly
                  overviewMap={new Map()}
                  editedSliceProducts={new Map()}
                  isHighlighted={false}
                  setOverviewMap={() => {}}
                  setEditedSliceProducts={() => {}}
                  setErroneousSliceProducts={() => {}}
                  setIsHighlighted={() => {}}
                  setRevokeDowntimeInterval={() => {}}
                  saveChanges={async () => {}}
                />
              </Suspense>
            )}
          </>
        )}
      </table>
      {(selectHandle.active || selectedCells.length > 1) && (
        <div
          data-testid="select-handler-wrapper"
          className={clsx(
            'pointer-events-none absolute z-30 box-border border-green-200 bg-green-200/5',
            selectHandle.selectionType === 'copy' ? 'border-2 border-dashed' : 'border-[3px] border-solid',
            {
              'bg-grey-300/10 border-grey-300':
                selectHandle.selectionType !== 'fill' &&
                (selectHandle.row !== selectHandle.targetRow || selectHandle.column !== selectHandle.targetColumn),
            },
          )}
          style={{
            left: `${visualHandle.left}px`,
            top: `${visualHandle.top}px`,
            width: `${visualHandle.width}px`,
            height: `${visualHandle.height}px`,
          }}
        />
      )}
    </div>
  );
}
